/**
 * `AgentCardProfileApi` — typed dashboard client for the merchant-
 * authoritative apply route (ADR-0114 Slice B2 + B3).
 *
 *   POST /v1/sites/:site_id/agent-card-profile
 *     If-Match: <etag from prior GET / apply response, or "*" for "no row yet">
 *     body: { fields: { ... }, source_hint?: { profile_extraction_id,
 *                                              suggestion_content_hash } }
 *     200 → { visibility: VisibilityReport, etag: string, updated_at: string }
 *     412 → ApiError('server_error') wrapped via the http-client's
 *           `statusToKind` (precondition_failed → server_error in the
 *           collapsed taxonomy — the bag catches the status field on
 *           ApiError and re-throws a discriminated `ApplyError` so the
 *           hook can branch precisely).
 *     400 → ApiError('bad_request') with `server_reason` carrying the
 *           offending field discriminator.
 *     403 → ApiError('forbidden').
 *     404 → ApiError('not_found').
 *     428 → ApiError('server_error') with `server_reason='if_match_required'`.
 *
 * The bag DOES NOT speak to the LLM extraction surface — its only
 * authority is the merchant-typed action (ADR-0114 §4.4 — the
 * no-auto-apply invariant on the dashboard side: the LLM result is
 * display data; the merchant's session is the write authority).
 *
 * Untrusted-rendering posture (B3 review surface): this module emits
 * no UI — the suggested `value` / `rationale` are rendered by the
 * suggestions modal as React JSX text nodes (auto-escaped) under a
 * "Suggested by Sill's site analyzer" badge. The bag handles wire
 * only; render-boundary safety lives in `SuggestionsModal.tsx`.
 *
 * Content-hash discipline (ADR-0114 §5.1): the `source_hint`'s
 * `suggestion_content_hash` is SHA-256-base64url over the displayed
 * suggestion as rendered to the merchant — computed dashboard-side
 * so the server's audit row records "what bytes the merchant
 * approved." {@link computeSuggestionContentHash} is the helper.
 */

import type { SiteId, VisibilityReport } from '@sill/types';

import { ApiError, type HttpClient } from './http-client.js';

/** The seven writable agent-card profile fields (the `name` field is
 *  set during onboarding, never via this route).
 *
 *  Mirrors `APPLY_FIELD_NAMES` on the server side; values are exactly
 *  the `ProfileFieldId` union minus `name`. Defensive: the validator
 *  on the server re-validates regardless. */
export const APPLY_FIELD_NAMES = [
  'description',
  'price_range',
  'currency',
  'shipping_zones',
  'return_window_days',
  'subscription_billing',
  'agent_card_authoritative',
] as const;
export type ApplyFieldName = (typeof APPLY_FIELD_NAMES)[number];

/** The forensic `source_hint` carried alongside an apply when the
 *  merchant accepted from a suggestion. Absent on manual edits. */
export interface ApplySourceHint {
  /** ULID prefix-shape `pe_<26 Crockford base32>`. */
  readonly profile_extraction_id: string;
  /** SHA-256 base64url (43 chars unpadded) over the displayed
   *  suggestion. {@link computeSuggestionContentHash} for the
   *  recipe; the server re-validates the shape. */
  readonly suggestion_content_hash: string;
}

export interface ApplyAgentCardProfileInput {
  readonly site_id: SiteId;
  /** Optimistic-concurrency token. `*` for "no row yet — INSERT".
   *  Concrete value is the `etag` from a prior GET or apply. */
  readonly etag: string;
  readonly fields: Partial<Record<ApplyFieldName, string>>;
  readonly source_hint?: ApplySourceHint;
}

export interface ApplyAgentCardProfileSuccess {
  readonly kind: 'ok';
  readonly visibility: VisibilityReport;
  /** New eTag the dashboard threads into the next If-Match. */
  readonly etag: string;
  readonly updated_at: string;
}

/** The closed taxonomy of failure modes the hook branches on. The
 *  discriminator maps to one specific dashboard banner / re-fetch
 *  path; collapsing them all to "error" would lose the 412 re-fetch
 *  affordance and the 403 role-explanation copy. */
export type ApplyAgentCardProfileFailureKind =
  /** 412 — another admin updated this site; modal stays open, hook
   *  signals "re-fetch + retry prompt". */
  | 'concurrency_mismatch'
  /** 403 — reviewer / viewer role. Modal closes; toast explains. */
  | 'forbidden'
  /** 400 — server-side validation failed. The view shows an inline
   *  error keyed off `server_reason` (e.g. `field_oversize`,
   *  `invalid_return_window_days_value`). */
  | 'validation'
  /** 404 — the site is unknown / cross-tenant / soft-deleted. */
  | 'not_found'
  /** 428 — If-Match header missing or malformed. The dashboard's
   *  bag always supplies one, so this is a defensive surface (a
   *  proxy strip-mid-flight). */
  | 'precondition_required'
  /** 401 — session expired; the http-client's on_unauthorized has
   *  already fired. The bag re-throws so the modal can close. */
  | 'unauthorized'
  /** Everything else: network blip, 5xx, malformed response. */
  | 'unknown';

export interface ApplyAgentCardProfileFailure {
  readonly kind: ApplyAgentCardProfileFailureKind;
  /** The server's `reason` discriminator when present (e.g.
   *  `field_oversize`, `if_match_mismatch`, `unknown_field`). */
  readonly server_reason?: string;
  /** The offending field name when the server populated one. */
  readonly field?: string;
}

export type ApplyAgentCardProfileResult =
  | ApplyAgentCardProfileSuccess
  | ApplyAgentCardProfileFailure;

/** Discriminator for {@link AgentCardProfileAPI.getEtag} — the dashboard
 *  branches on `kind` to thread the recovered eTag back into the apply
 *  bag. The shape mirrors {@link ApplyAgentCardProfileResult} so callers
 *  can reuse the same error-banner copy on the recovery path. */
export type GetAgentCardProfileEtagResult =
  | {
      readonly kind: 'ok';
      /** The current eTag for the merchant's row, or `'*'` when no
       *  row exists yet (the apply route will INSERT). */
      readonly etag: string;
      /** True when the GET found an existing row; false when the
       *  dashboard should use `If-Match: '*'` on its next apply. */
      readonly present: boolean;
    }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'unknown' };

export interface AgentCardProfileAPI {
  apply(
    input: ApplyAgentCardProfileInput,
  ): Promise<ApplyAgentCardProfileResult>;
  /**
   * Read the current eTag for a site's `agent_card_profile` row
   * (ADR-0114 Slice B3 — security review Minor 1, 2026-06-16). The
   * dashboard's stale-eTag recovery path (a 412 on apply because
   * another admin updated the row first) calls this to fetch the
   * REAL current eTag, then retries the apply with the fresh value.
   *
   * Resetting the in-memory eTag to `'*'` after a 412 would have
   * been a permanent stalemate — `'*'` is INSERT-only semantics and
   * the row already exists. The route correctly returns 412 again,
   * forever; the merchant has no recovery short of a full page
   * reload. This GET is the one-round-trip fix.
   */
  getEtag(site_id: SiteId): Promise<GetAgentCardProfileEtagResult>;
}

export interface AgentCardProfileDeps {
  readonly http: HttpClient;
}

interface ApplyWireSuccess {
  visibility: VisibilityReport;
  etag: string;
  updated_at: string;
}

interface GetEtagWireSuccess {
  etag: string;
  present: boolean;
}

/**
 * Compute the SHA-256 base64url digest of a suggestion's displayed
 * bytes. Dashboard-side hash so the server can correlate "what bytes
 * we showed the merchant" with "what they applied" — a defense
 * against a dashboard render-vs-apply mismatch (ADR-0114 §5.1).
 *
 * Canonical-JSON-ish: stringify the {field_id, value, rationale}
 * triples in a stable order so two browsers that displayed the same
 * suggestion emit the same hash. We sort by `field_id` (closed
 * `ProfileFieldId` enum, deterministic across locales) and emit the
 * minimal JSON shape. The server treats this as opaque forensics — a
 * shape mismatch is a 400, not a security failure.
 */
export async function computeSuggestionContentHash(
  fields: readonly { field_id: string; value: string; rationale: string }[],
): Promise<string> {
  // Stable sort by field_id so the hash is order-independent on the
  // wire — the modal renders in catalog order but a future re-order
  // of the array must not break the hash equality check.
  const sorted = [...fields].sort((a, b) =>
    a.field_id < b.field_id ? -1 : a.field_id > b.field_id ? 1 : 0,
  );
  const canonical = JSON.stringify(
    sorted.map((f) => ({
      field_id: f.field_id,
      value: f.value,
      rationale: f.rationale,
    })),
  );
  const bytes = new TextEncoder().encode(canonical);
  // Web Crypto digest — available in modern browsers + jsdom test env
  // (Node 20+ ships `crypto.subtle` on `globalThis.crypto`).
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return base64UrlNoPad(new Uint8Array(digest));
}

function base64UrlNoPad(bytes: Uint8Array): string {
  // btoa expects a binary string; build it from the byte array.
  let bin = '';
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i] as number);
  }
  const b64 = (globalThis.btoa ?? nodeBtoa)(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function nodeBtoa(bin: string): string {
  // Defensive fallback for non-browser test envs that lack btoa.
  return Buffer.from(bin, 'binary').toString('base64');
}

export function createAgentCardProfileApi(
  deps: AgentCardProfileDeps,
): AgentCardProfileAPI {
  return {
    async apply(
      input: ApplyAgentCardProfileInput,
    ): Promise<ApplyAgentCardProfileResult> {
      // The route REQUIRES an `If-Match` header (428 if missing). The
      // http-client's `extra_headers` field threads it through to the
      // underlying fetch. The route returns the new eTag in BOTH the
      // ETag response header AND the body's `etag` field — the bag
      // reads from the body (the http-client doesn't expose response
      // headers).
      const body: Record<string, unknown> = {
        fields: input.fields,
      };
      if (input.source_hint !== undefined) {
        body.source_hint = input.source_hint;
      }
      try {
        const res = await deps.http.request<ApplyWireSuccess>({
          method: 'POST',
          path: `/v1/sites/${encodeURIComponent(input.site_id)}/agent-card-profile`,
          body,
          extra_headers: { 'if-match': input.etag },
        });
        if (res === undefined) {
          return { kind: 'unknown' };
        }
        return {
          kind: 'ok',
          visibility: res.visibility,
          etag: res.etag,
          updated_at: res.updated_at,
        };
      } catch (err) {
        return mapApplyError(err);
      }
    },
    async getEtag(site_id: SiteId): Promise<GetAgentCardProfileEtagResult> {
      // ADR-0114 Slice B3 — security review Minor 1 (2026-06-16). The
      // 412 stale-eTag recovery path calls this BEFORE re-loading
      // suggestions so the retry apply targets the existing row with
      // the row's CURRENT eTag (not `'*'`, which would 412 again
      // forever — INSERT-only semantics).
      try {
        const res = await deps.http.request<GetEtagWireSuccess>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(site_id)}/agent-card-profile`,
        });
        if (res === undefined) {
          return { kind: 'unknown' };
        }
        // Defensive: a misconfigured stub or a wire-shape regression
        // could return a body missing `etag`. Treat that as 'unknown'
        // rather than setting `etag = undefined` in the dashboard's
        // state — the apply would then send `If-Match: undefined` and
        // the route would 412 the merchant into a stalemate.
        if (typeof res.etag !== 'string') {
          return { kind: 'unknown' };
        }
        return {
          kind: 'ok',
          etag: res.etag,
          present: res.present === true,
        };
      } catch (err) {
        return mapGetEtagError(err);
      }
    },
  };
}

/**
 * Map an http-client error to the closed `GetAgentCardProfileEtagResult`
 * failure shape. The dashboard's recovery path branches on `kind` so
 * the same banner copy as `apply` covers 403/404/401; everything else
 * collapses to `unknown` (the modal then closes the recovery loop with
 * the same toast as the apply path's catch-all).
 */
export function mapGetEtagError(err: unknown): GetAgentCardProfileEtagResult {
  if (err instanceof ApiError) {
    if (err.kind === 'forbidden') return { kind: 'forbidden' };
    if (err.kind === 'not_found') return { kind: 'not_found' };
    if (err.kind === 'unauthorized') return { kind: 'unauthorized' };
  }
  if (
    typeof err === 'object'
    && err !== null
    && 'name' in err
    && (err as { name?: string }).name === 'AuthUnauthorizedError'
  ) {
    return { kind: 'unauthorized' };
  }
  return { kind: 'unknown' };
}

/**
 * Map an http-client error to the closed `ApplyAgentCardProfileFailure`
 * shape. The hook branches on `kind`; the modal renders the right
 * banner from the discriminator + the server_reason.
 */
export function mapApplyError(err: unknown): ApplyAgentCardProfileFailure {
  if (err instanceof ApiError) {
    // 412 collapses to `server_error` in the http-client's taxonomy
    // (statusToKind has no `precondition_failed` branch), so we read
    // the raw `status` to distinguish. Same for 428.
    if (err.status === 412) {
      return {
        kind: 'concurrency_mismatch',
        ...(err.server_reason !== undefined
          ? { server_reason: err.server_reason }
          : {}),
      };
    }
    if (err.status === 428) {
      return {
        kind: 'precondition_required',
        ...(err.server_reason !== undefined
          ? { server_reason: err.server_reason }
          : {}),
      };
    }
    if (err.kind === 'forbidden') {
      return { kind: 'forbidden' };
    }
    if (err.kind === 'not_found') {
      return { kind: 'not_found' };
    }
    if (err.kind === 'bad_request') {
      return {
        kind: 'validation',
        ...(err.server_reason !== undefined
          ? { server_reason: err.server_reason }
          : {}),
      };
    }
    if (err.kind === 'unauthorized') {
      return { kind: 'unauthorized' };
    }
  }
  // AuthUnauthorizedError from the http-client doesn't extend ApiError;
  // catch it by name to keep the hook clean.
  if (
    typeof err === 'object'
    && err !== null
    && 'name' in err
    && (err as { name?: string }).name === 'AuthUnauthorizedError'
  ) {
    return { kind: 'unauthorized' };
  }
  return { kind: 'unknown' };
}
